import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import TrainerCard from "@/components/TrainerCard";
import { Search, Filter, X, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const PRICE_STEPS = [0, 20, 40, 60, 80, 100, 200];

const Pill = ({ active, children, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick} className={`pill ${active ? "pill-active" : ""}`}>{children}</button>
);

function FiltersContent({ meta, county, setCounty, goal, setGoal, speciality, setSpeciality, gender, setGender, maxPrice, setMaxPrice, online, setOnline, city, setCity }) {
  return (
    <>
      <div>
        <div className="flex items-center gap-2 text-sm font-bold mb-3"><Filter size={14}/> Filters</div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">County</div>
        <div className="flex flex-wrap gap-2">
          {(meta?.counties || []).map((c) => (
            <Pill key={c} testId={`filter-county-${c}`} active={county === c} onClick={() => setCounty(county === c ? "" : c)}>{c}</Pill>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">Goal</div>
        <div className="flex flex-wrap gap-2">
          {(meta?.goals || []).map((g) => (
            <Pill key={g} testId={`filter-goal-${g.replace(/\s+/g,'-').toLowerCase()}`} active={goal === g} onClick={() => setGoal(goal === g ? "" : g)}>{g}</Pill>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">Speciality</div>
        <div className="flex flex-wrap gap-2">
          {(meta?.specialities || []).map((s) => (
            <Pill key={s} testId={`filter-speciality-${s.replace(/\s+/g,'-').toLowerCase()}`} active={speciality === s} onClick={() => setSpeciality(speciality === s ? "" : s)}>{s}</Pill>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">Gender</div>
        <div className="flex gap-2">
          {["Female", "Male"].map((g) => (
            <Pill key={g} testId={`filter-gender-${g.toLowerCase()}`} active={gender === g} onClick={() => setGender(gender === g ? "" : g)}>{g}</Pill>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">Max price / hour</div>
        <input data-testid="filter-price-range" type="range" min={20} max={200} step={10} value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full accent-emerald-600"/>
        <div className="flex justify-between text-xs text-neutral-500 mt-1">
          {PRICE_STEPS.map((p) => <span key={p}>€{p}</span>)}
        </div>
        <div className="text-sm mt-2">Up to <b>€{maxPrice}</b></div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input data-testid="filter-online" type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} className="accent-emerald-600"/>
        Online coaching
      </label>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-3">City</div>
        <input data-testid="filter-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Dublin" className="h-10 w-full rounded-md border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-900"/>
      </div>
    </>
  );
}

export default function TrainersPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [county, setCounty] = useState(params.get("county") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [goal, setGoal] = useState(params.get("goal") || "");
  const [speciality, setSpeciality] = useState(params.get("speciality") || "");
  const [gender, setGender] = useState(params.get("gender") || "");
  const [maxPrice, setMaxPrice] = useState(Number(params.get("max_price") || 200));
  const [online, setOnline] = useState(params.get("online") === "true");
  const [sort, setSort] = useState(params.get("sort") || "rating");

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: async () => (await api.get("/meta/goals")).data });

  const query = {
    q: q || undefined,
    county: county || undefined,
    city: city || undefined,
    goal: goal || undefined,
    speciality: speciality || undefined,
    gender: gender || undefined,
    max_price: maxPrice < 200 ? maxPrice : undefined,
    online: online ? true : undefined,
    sort: sort || undefined,
  };
  const key = JSON.stringify(query);
  const { data: trainers = [], isLoading } = useQuery({
    queryKey: ["trainers", key],
    queryFn: async () => (await api.get("/trainers", { params: query })).data,
  });

  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => { if (v !== undefined && v !== "" && v !== false) p.set(k, String(v)); });
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const clearAll = () => {
    setQ(""); setCounty(""); setCity(""); setGoal(""); setSpeciality(""); setGender(""); setMaxPrice(200); setOnline(false); setSort("rating");
  };

  return (
    <div className="container-fl py-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Marketplace</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Personal Trainers in Ireland</h1>
          <p className="text-neutral-500 mt-1 text-sm">{trainers.length} verified trainers match your filters</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-neutral-400"/>
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8 pr-3 h-10 rounded-md border border-neutral-200 focus:border-neutral-900 outline-none text-sm bg-white"/>
          </div>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
                  className="h-10 rounded-md border border-neutral-200 focus:border-neutral-900 outline-none text-sm bg-white px-3">
            <option value="rating">Top rated</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="newest">Newest</option>
          </select>
          <button data-testid="clear-filters-btn" onClick={clearAll} className="btn-secondary"><X size={14}/> Clear</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters sidebar — desktop */}
        <aside className="hidden lg:block space-y-6" data-testid="filters-sidebar">
          <FiltersContent meta={meta} county={county} setCounty={setCounty} goal={goal} setGoal={setGoal}
                          speciality={speciality} setSpeciality={setSpeciality} gender={gender} setGender={setGender}
                          maxPrice={maxPrice} setMaxPrice={setMaxPrice} online={online} setOnline={setOnline}
                          city={city} setCity={setCity} />
        </aside>

        {/* Mobile filter trigger + sheet */}
        <div className="lg:hidden -mt-4 mb-4">
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-filters-btn" className="btn-secondary w-full justify-center">
                <SlidersHorizontal size={14}/> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                <FiltersContent meta={meta} county={county} setCounty={setCounty} goal={goal} setGoal={setGoal}
                                speciality={speciality} setSpeciality={setSpeciality} gender={gender} setGender={setGender}
                                maxPrice={maxPrice} setMaxPrice={setMaxPrice} online={online} setOnline={setOnline}
                                city={city} setCity={setCity} />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Results */}
        <section>
          {isLoading ? (
            <div className="text-neutral-500 py-20 text-center">Loading trainers…</div>
          ) : trainers.length === 0 ? (
            <div data-testid="empty-state" className="text-center border border-dashed border-neutral-200 rounded-xl bg-white py-20 px-6">
              <div className="w-16 h-16 rounded-full bg-neutral-100 grid place-items-center mx-auto mb-4">
                <Search size={24} className="text-neutral-400"/>
              </div>
              <h3 className="text-xl font-bold mb-2">No trainers match those filters</h3>
              <p className="text-neutral-500 text-sm mb-6 max-w-sm mx-auto">
                Try removing a filter, widening the price range, or searching a different county.
              </p>
              <button onClick={clearAll} className="btn-primary" data-testid="empty-state-clear">
                <X size={14}/> Clear all filters
              </button>
            </div>
          ) : (
            <div data-testid="trainers-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {trainers.map((t) => <TrainerCard key={t.id} t={t}/>)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
